import React, { useState, useMemo } from 'react';
import { Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft, faShieldAlt, faUserTag,
  faSearch, faPlus
} from '@fortawesome/free-solid-svg-icons';
import { useUserPermissions, useResourceTypes } from '../hooks/useAuthorizationData';
import CapabilityAssignDrawer from './CapabilityAssignDrawer';
import PermissionsTree from './PermissionsTree'; // extract from UserPermissionsPage or inline below
import AssignCapabilitiesTree from './AssignCapabilitiesTree'; // same

const RoleDetailPage = ({ role, userId, onBack, onAssigned }) => {
  const { data: permissions, isLoading, refetch } = useUserPermissions(userId);
  const { data: resourceTypes } = useResourceTypes();

  const [activeTab, setActiveTab] = useState('overview');
  const [capSearch, setCapSearch] = useState('');
  const [drawerCap, setDrawerCap] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const resourceTypeMap = useMemo(() => {
    const map = {};
    (resourceTypes || []).forEach(rt => { map[rt.name] = rt; });
    return map;
  }, [resourceTypes]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  // Same as UserPermissionsPage overview
  const roleAssignments = useMemo(
    () => (permissions || []).filter(p => p.roleId),
    [permissions]
  );
  const directCaps = useMemo(
    () => (permissions || []).filter(p => !p.roleId),
    [permissions]
  );

  // Capabilities tab — filtered to only this role's capabilities
  const roleCaps = role?.capabilities || [];
  const filteredRoleCaps = roleCaps.filter(c =>
    !capSearch ||
    c.name.toLowerCase().includes(capSearch.toLowerCase()) ||
    (c.targetResourceType || '').toLowerCase().includes(capSearch.toLowerCase())
  );

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'capabilities', label: 'Capabilities' },
  ];

  if (isLoading) {
    return (
      <div className="auth-loading">
        <Spinner animation="border" style={{ color: '#3f88a5' }} />
        <p>Loading permissions...</p>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="user-perm-header">
        <button className="auth-back-btn" onClick={onBack}>
          <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
          Back to Roles
        </button>
        <div className="user-perm-identity">
          <div className="user-avatar-lg">
            <FontAwesomeIcon icon={faUserTag} />
          </div>
          <div>
            <h4>{role.name}</h4>
            {role.description && (
              <p className="text-muted mb-0">{role.description}</p>
            )}
          </div>
        </div>
      </div>

      {successMsg && (
        <Alert variant="success" className="auth-success-alert">{successMsg}</Alert>
      )}

      {/* Stats */}
      <div className="perm-stats-row">
        <div className="perm-stat-card">
          <span className="perm-stat-number">{roleAssignments.length}</span>
          <span className="perm-stat-label">Role Assignments</span>
        </div>
        <div className="perm-stat-card">
          <span className="perm-stat-number">{directCaps.length}</span>
          <span className="perm-stat-label">Direct Capabilities</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="perm-tab-bar">
        {tabs.map(t => (
          <button
            key={t.key}
            className={`perm-tab-btn ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview — exact same as UserPermissionsPage */}
      {activeTab === 'overview' && (
        <div className="perm-section">
          {roleAssignments.length === 0 && directCaps.length === 0 ? (
            <div className="auth-empty-section">
              <FontAwesomeIcon icon={faShieldAlt} />
              <p>No permissions assigned yet</p>
              <Button
                className="auth-btn-primary"
                onClick={() => setActiveTab('capabilities')}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Assign Capability
              </Button>
            </div>
          ) : (
            <>
              {roleAssignments.length > 0 && (
                <div className="mb-4">
                  <div className="perm-overview-section-title">
                    <FontAwesomeIcon icon={faUserTag} className="me-2" />
                    Assigned Roles
                  </div>
                  <PermissionsTree assignments={roleAssignments} type="role" />
                </div>
              )}
              {directCaps.length > 0 && (
                <div>
                  <div className="perm-overview-section-title">
                    <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                    Direct Capabilities
                  </div>
                  <PermissionsTree assignments={directCaps} type="capability" />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Capabilities — role's caps only, opens CapabilityAssignDrawer */}
      {activeTab === 'capabilities' && (
        <div className="perm-section">
          <div className="perm-section-search">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search capabilities..."
              value={capSearch}
              onChange={e => setCapSearch(e.target.value)}
              className="perm-search-input"
            />
          </div>
          <AssignCapabilitiesTree
            capabilities={filteredRoleCaps}
            onAssign={cap => setDrawerCap(cap)}
            search={capSearch}
          />
        </div>
      )}

      {/* Same drawer as UserPermissionsPage */}
      <CapabilityAssignDrawer
        show={!!drawerCap}
        onHide={() => setDrawerCap(null)}
        capability={drawerCap}
        resourceTypeMap={resourceTypeMap}
        userId={userId}
        onAssigned={() => {
          refetch();
          showSuccess(`Capability "${drawerCap?.name}" assigned`);
          setDrawerCap(null);
          onAssigned?.(`Capability "${drawerCap?.name}" assigned`);
        }}
      />
    </>
  );
};

export default RoleDetailPage;